<template>
  <div v-show="changesCount" class="history-block__container">
    <div v-show="!(isLastBlock && !isHistoryOpen)" class="vertical-row vertical-row_first" />
    <div class="history-item_container mb-12">
      <div class="history-item_icon">
        <span v-if="hasUnpublished" class="pulse" />
        <Icon v-else color="success" size="20" type="check-circle" />
      </div>
      <div class="history-item_info spacey-4">
        <div class="history-item_info__title">{{ title }}</div>
        <div class="history-item_info__subtitle">{{ subtitle }}</div>
      </div>
      <div class="history-item_actions">
        <Button
          v-if="hasUnpublished && !currentList"
          type="secondary"
          size="small"
          :loading="publishing"
          right-icon="cloud-upload"
          @click="$emit('publish')"
        >
          {{ $t('Publish') }}
        </Button>
        <Button
          v-if="!hasUnpublished && item.xlsx_file"
          type="ghost"
          size="small"
          icon="excel"
          :loading="historyDownloading"
          @click="handleExcelDownload"
        />
        <InfoTooltip v-else-if="!hasUnpublished && fileMayBeNotUploaded">
          <template #element>
            <Button type="ghost" size="small" icon="excel" disabled />
          </template>
          <template #default>
            {{ $t('The file will be available soon') }}
          </template>
        </InfoTooltip>
        <Button
          type="ghost"
          size="small"
          icon="chevron-down"
          :reverse-icon="!isHistoryOpen"
          @click="handleHistoryToggle"
        />
      </div>
    </div>
    <template v-if="isHistoryOpen">
      <div v-for="(change, idx) in item.changes" :key="idx" class="history-item_with-dot">
        <div class="history-item__dot" />
        <div
          class="vertical-row"
          :class="{ 'vertical-row_last': idx === item.changes.length - 1 && isLastBlock }"
        />
        <HistoryItem class="mb-12" :change="change" />
      </div>
    </template>
  </div>
</template>

<script>
  import Icon from '@/components/ui/Icon';
  import Button from '@/components/ui-v2/Button';
  import HistoryItem from '@/components/project/history/HistoryItem';
  import timeAgo from '@/utils/date/time-ago';
  import downloadByLink from '@/utils/download-by-link';
  import InfoTooltip from '@/components/ui/InfoTooltip';

  export default {
    name: 'HistoryBlock',
    components: {
      Icon,
      Button,
      HistoryItem,
      InfoTooltip,
    },
    props: {
      item: {
        type: Object,
        required: true,
      },
      isLastBlock: {
        type: Boolean,
        default: false,
      },
      currentList: {
        type: Number,
        default: 0,
      },
      publishing: {
        type: Boolean,
        default: false,
      },
      fileMayBeNotUploaded: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        isHistoryOpen: true,
        historyDownloading: false,
      };
    },
    computed: {
      hasUnpublished() {
        return !('id' in this.item);
      },
      title() {
        return this.hasUnpublished
          ? this.$t('Unpublished changes')
          : `${this.$t('Published')} ${this.changesCount} ${this.$tc('change', this.changesCount)}`;
      },
      subtitle() {
        return this.hasUnpublished
          ? `${this.changesCount} ${this.$tc('change', this.changesCount)}`
          : this.publisherInfo(this.item);
      },
      changesCount() {
        return this.item?.changes.length || 0;
      },
    },
    methods: {
      handleHistoryToggle() {
        this.isHistoryOpen = !this.isHistoryOpen;
      },
      publisherInfo(item) {
        return `${item?.user?.name}, ${timeAgo(new Date(item?.created_at))}`;
      },
      async handleExcelDownload() {
        this.historyDownloading = true;
        try {
          await downloadByLink({
            link: this.item.xlsx_file,
            fileName: `Project ${new Date(this.item.created_at).toLocaleString()}.xlsx`,
            withoutAuthorization: true,
          });
        } catch (e) {
          console.error(e);
        }
        this.historyDownloading = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import 'src/styles/colors';

  .history-block__container {
    position: relative;
  }

  .vertical-row {
    height: 100%;
    width: 2px;
    position: absolute;
    left: 29px;
    background-color: $surface-border;
    &_first {
      height: 80px;
    }
    &_last {
      height: 15px;
    }
  }

  .pulse {
    display: block;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: #3581f3;
    cursor: pointer;
    box-shadow: 0 0 0 rgba(215, 230, 253, 1);
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% {
      -moz-box-shadow: 0 0 0 0 rgba(215, 230, 253, 1);
      box-shadow: 0 0 0 0 rgba(215, 230, 253, 1);
    }
    70% {
      -moz-box-shadow: 0 0 0 10px rgba(215, 230, 253, 0.5);
      box-shadow: 0 0 0 10px rgba(215, 230, 253, 0.5);
    }
    100% {
      -moz-box-shadow: 0 0 0 0 rgba(215, 230, 253, 0);
      box-shadow: 0 0 0 0 rgba(215, 230, 253, 0);
    }
  }

  .history-item {
    &_with-dot {
      position: relative;
      display: grid;
      grid-template-columns: 40px 1fr;
    }
    &__dot {
      height: 8px;
      width: 8px;
      background-color: $surface-border;
      border-radius: 50%;
      position: relative;
      top: 15px;
      left: 26px;
    }
    &_icon {
      width: 40px;
      display: flex;
      justify-content: center;
    }
    &_container {
      border: 1px solid $surface-border;
      box-sizing: border-box;
      border-radius: 8px;
      background-color: white;
      z-index: 1;
      position: relative;

      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 8px 10px 8px 10px;
    }
    &_info {
      margin-left: 8px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      &__title {
        color: $text-primary;
        size: 14px;
      }
      &__subtitle {
        color: $text-tertiary;
        size: 12px;
      }
    }
    &_actions {
      display: flex;
      gap: 8px;
      margin-left: auto;
    }
  }
</style>
